import { useForm } from 'react-hook-form';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useRenameOrgMutation } from '#api/org.queries.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input } from '#components/atoms/forms/index.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { ModalBody, ModalFooter } from '#components/patterns/modal/index.ts';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';

const FORM_NAME = 'rename-org-form';

type FormData = {
  name: string;
};

type Props = {
  orgId: string;
  defaultValues: FormData;
  onSuccess: () => void;
  onCancel: () => void;
};

/**
 * {@link validateOrgName} returns an error message for an invalid organization name, or `true`.
 * Mirrors the api: 1 to 150 characters counted in code points rather than UTF-16 units, no
 * leading or trailing whitespace, and no control characters (Unicode category Cc).
 */
function validateOrgName(name: string): string | true {
  // eslint-disable-next-line @typescript-eslint/no-misused-spread -- code points are what the api counts, matching MySQL VARCHAR
  const length = [...name].length;
  if (length < 1 || length > 150) return 'Name must be between 1 and 150 characters';
  if (name.trim() !== name) return 'Name cannot include leading or trailing whitespace';
  if (/\p{Cc}/u.test(name)) return 'Name cannot include line breaks or other control characters';
  return true;
}

/**
 * {@link RenameOrgForm} allows updating the name of the current organization.
 */
export function RenameOrgForm({ orgId, defaultValues, onSuccess, onCancel }: Props) {
  const { register, handleSubmit, formState, setError } = useForm<FormData>({ defaultValues });
  const renameOrg = useRenameOrgMutation();
  const { errors, isSubmitting } = formState;

  const onSubmit = handleSubmit(async (data) => {
    try {
      await renameOrg.mutateAsync({ orgId, name: data.name });
      onSuccess();
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          switch (path) {
            case 'name':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        trackError(error);
        setError('root.global', { message: 'We couldn’t rename the organization, please try again' });
      }
    }
  });

  return (
    <>
      <ModalBody>
        <form method="post" id={FORM_NAME} onSubmit={onSubmit}>
          <Spacer direction="vertical" size="wide" className="mb-16">
            {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
            <Field label="Name">
              <Input
                className="width-100"
                hasError={Boolean(errors.name)}
                {...register('name', { validate: validateOrgName })}
              />
              {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
            </Field>
          </Spacer>
        </form>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form={FORM_NAME} disabled={isSubmitting}>
              Save
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </>
  );
}
